import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  Receipt as TaxIcon,
  Download as DownloadIcon,
  Print as PrintIcon,
  Refresh as RefreshIcon,
  TrendingUp,
  Assessment,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import InvoiceService from '../../services/invoice.service';

const INVOICE_TYPE_LABELS: Record<string, string> = {
  customer: 'Customer',
  insurance: 'Insurance',
  service_provider: 'Service Provider',
  insurance_customer: 'Insurance Customer',
  insurance_company_monthly: 'Insurance Company Monthly',
  service_provider_monthly: 'Service Provider Monthly',
  service_provider_to_darb: 'Service Provider to Darb',
};

// Filter dropdown excludes monthly types
const INVOICE_TYPE_FILTER_OPTIONS = Object.entries(INVOICE_TYPE_LABELS).filter(
  ([key]) => !key.includes('monthly')
);

const formatCurrency = (amount: number) => {
  if (amount == null || isNaN(amount)) return 'OMR 0.00';
  return `OMR ${Number(amount).toFixed(2)}`;
};

const formatDate = (dateStr: string) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString();
};

const TaxReport: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();
  const [startDate, setStartDate] = useState(`${currentYear}-01-01`);
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [invoiceTypeFilter, setInvoiceTypeFilter] = useState<string>('all');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invoices, setInvoices] = useState<any[]>([]);
  const [summary, setSummary] = useState<{
    totalTax: number;
    totalInvoiced: number;
    invoiceCount: number;
    byType: Record<string, { tax: number; count: number; total: number }>;
    byMonth: Record<string, { tax: number; count: number }>;
  }>({ totalTax: 0, totalInvoiced: 0, invoiceCount: 0, byType: {}, byMonth: {} });

  const fetchInvoices = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const params: any = {
        'pagination[pageSize]': 2000,
        'pagination[page]': 1,
        sort: 'invoiceDate:desc',
        'filters[invoiceDate][$gte]': `${startDate}T00:00:00.000Z`,
        'filters[invoiceDate][$lte]': `${endDate}T23:59:59.999Z`,
      };
      if (statusFilter && statusFilter !== 'all') {
        params['filters[status][$eq]'] = statusFilter;
      }
      if (invoiceTypeFilter && invoiceTypeFilter !== 'all') {
        params['filters[invoiceType][$eq]'] = invoiceTypeFilter;
      }
      const response = await InvoiceService.getAll(params);
      const data = response.data?.data || [];
      setInvoices(Array.isArray(data) ? data : []);

      // Aggregate tax summary
      const list = Array.isArray(data) ? data : [];
      let totalTax = 0;
      let totalInvoiced = 0;
      const byType: Record<string, { tax: number; count: number; total: number }> = {};
      const byMonth: Record<string, { tax: number; count: number }> = {};

      list.forEach((inv: any) => {
        const attrs = inv.attributes || inv;
        const tax = Number(attrs.tax) || 0;
        const total = Number(attrs.total) || 0;
        totalTax += tax;
        totalInvoiced += total;

        const type = attrs.invoiceType || 'customer';
        if (!byType[type]) byType[type] = { tax: 0, count: 0, total: 0 };
        byType[type].tax += tax;
        byType[type].count += 1;
        byType[type].total += total;

        const date = attrs.invoiceDate;
        if (date) {
          const monthKey = date.slice(0, 7); // YYYY-MM
          if (!byMonth[monthKey]) byMonth[monthKey] = { tax: 0, count: 0 };
          byMonth[monthKey].tax += tax;
          byMonth[monthKey].count += 1;
        }
      });

      setSummary({
        totalTax,
        totalInvoiced,
        invoiceCount: list.length,
        byType,
        byMonth,
      });
    } catch (err: any) {
      setError(err?.message || 'Failed to load tax data');
      setInvoices([]);
      setSummary({ totalTax: 0, totalInvoiced: 0, invoiceCount: 0, byType: {}, byMonth: {} });
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, statusFilter, invoiceTypeFilter]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  const getAttr = (inv: any) => inv?.attributes || inv;

  const handleExportCSV = () => {
    const headers = ['Invoice #', 'Date', 'Type', 'Status', 'Subtotal', 'Tax', 'Total', 'Tax Rate'];
    const rows = invoices.map((inv) => {
      const a = getAttr(inv);
      return [
        a.invoiceNumber || '',
        a.invoiceDate || '',
        INVOICE_TYPE_LABELS[a.invoiceType] || a.invoiceType || '',
        a.status || '',
        a.subtotal ?? '',
        a.tax ?? '',
        a.total ?? '',
        a.taxRate != null ? (Number(a.taxRate) > 1 ? `${a.taxRate}%` : `${(Number(a.taxRate) * 100).toFixed(1)}%`) : '',
      ];
    });
    const csv = [headers.join(','), ...rows.map((r) => r.join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `tax-report-${startDate}-to-${endDate}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const monthOrder = Object.keys(summary.byMonth).sort();

  const tableByTypeRef = useRef<HTMLDivElement>(null);
  const tableByMonthRef = useRef<HTMLDivElement>(null);
  const tableDetailRef = useRef<HTMLDivElement>(null);

  const handlePrintTable = (ref: React.RefObject<HTMLDivElement | null>, title: string) => {
    if (!ref.current) return;
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    const content = ref.current.innerHTML;
    printWindow.document.write(`
      <html><head><title>${title}</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 20px; }
        table { border-collapse: collapse; width: 100%; }
        th, td { border: 1px solid #ddd; padding: 8px; text-align: left; }
        th { background-color: #f5f5f5; font-weight: 600; }
        h2 { margin-bottom: 12px; }
        .MuiChip-root { display: inline-block; padding: 2px 8px; border-radius: 12px; font-size: 12px; }
        .no-print { margin-bottom: 12px; }
        @media print { .no-print { display: none !important; } }
      </style></head><body>
      <div class="no-print">
        <button onclick="window.print()" style="padding: 8px 16px; cursor: pointer; margin-right: 8px;">Print</button>
        <button onclick="window.close()" style="padding: 8px 16px; cursor: pointer;">Close</button>
      </div>
      <h2>${title}</h2>
      ${content}
      </body></html>
    `);
    printWindow.document.close();
    printWindow.focus();
    // Wait for the new window to render before opening print dialog (prevents blank/flash-close)
    setTimeout(() => {
      printWindow.print();
    }, 250);
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2} mb={3}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TaxIcon sx={{ fontSize: 32 }} />
          {t('taxReport.title', 'Tax Report (Invoices)')}
        </Typography>
        <Stack direction="row" spacing={2} flexWrap="wrap">
          <Button variant="outlined" startIcon={<DownloadIcon />} onClick={handleExportCSV}>
            {t('taxReport.exportCsv', 'Export CSV')}
          </Button>
          <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchInvoices} disabled={loading}>
            {t('common.refresh', 'Refresh')}
          </Button>
        </Stack>
      </Stack>

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }} className="no-print">
        <Typography variant="subtitle1" gutterBottom fontWeight={600}>
          {t('taxReport.filters', 'Filters')}
        </Typography>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" flexWrap="wrap">
          <TextField
            label={t('taxReport.startDate', 'Start Date')}
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <TextField
            label={t('taxReport.endDate', 'End Date')}
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            sx={{ minWidth: 160 }}
          />
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>{t('taxReport.status', 'Status')}</InputLabel>
            <Select
              value={statusFilter}
              label={t('taxReport.status', 'Status')}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <MenuItem value="all">{t('taxReport.allStatuses', 'All')}</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
              <MenuItem value="sent">Sent</MenuItem>
              <MenuItem value="partiallyPaid">Partially Paid</MenuItem>
              <MenuItem value="paid">Paid</MenuItem>
              <MenuItem value="overdue">Overdue</MenuItem>
              <MenuItem value="cancelled">Cancelled</MenuItem>
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('taxReport.invoiceType', 'Invoice Type')}</InputLabel>
            <Select
              value={invoiceTypeFilter}
              label={t('taxReport.invoiceType', 'Invoice Type')}
              onChange={(e) => setInvoiceTypeFilter(e.target.value)}
            >
              <MenuItem value="all">{t('taxReport.allTypes', 'All Types')}</MenuItem>
              {INVOICE_TYPE_FILTER_OPTIONS.map(([value, label]) => (
                <MenuItem key={value} value={value}>{label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button variant="contained" onClick={fetchInvoices} disabled={loading}>
            {loading ? <CircularProgress size={24} /> : t('taxReport.apply', 'Apply')}
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary cards */}
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3} flexWrap="wrap">
        <Card sx={{ minWidth: 180, flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <Assessment color="primary" />
              <Typography color="text.secondary" variant="body2">
                {t('taxReport.totalTax', 'Total Tax (Period)')}
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(summary.totalTax)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, flex: 1 }}>
          <CardContent>
            <Stack direction="row" alignItems="center" spacing={1} mb={0.5}>
              <TrendingUp color="success" />
              <Typography color="text.secondary" variant="body2">
                {t('taxReport.totalInvoiced', 'Total Invoiced')}
              </Typography>
            </Stack>
            <Typography variant="h5" fontWeight={700}>
              {formatCurrency(summary.totalInvoiced)}
            </Typography>
          </CardContent>
        </Card>
        <Card sx={{ minWidth: 180, flex: 1 }}>
          <CardContent>
            <Typography color="text.secondary" variant="body2" mb={0.5}>
              {t('taxReport.invoiceCount', 'Invoices')}
            </Typography>
            <Typography variant="h5" fontWeight={700}>
              {summary.invoiceCount}
            </Typography>
          </CardContent>
        </Card>
      </Stack>

      {/* Tax by invoice type */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            {t('taxReport.taxByType', 'Tax by Invoice Type')}
          </Typography>
          <Tooltip title={t('common.print', 'Print')}>
            <IconButton size="small" onClick={() => handlePrintTable(tableByTypeRef, t('taxReport.taxByType', 'Tax by Invoice Type'))}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        <TableContainer ref={tableByTypeRef}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t('taxReport.type', 'Type')}</TableCell>
                <TableCell align="right">{t('taxReport.invoiceCount', 'Invoices')}</TableCell>
                <TableCell align="right">{t('taxReport.totalTax', 'Total Tax')}</TableCell>
                <TableCell align="right">{t('taxReport.totalInvoiced', 'Total')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {Object.entries(summary.byType)
                .sort((a, b) => b[1].tax - a[1].tax)
                .map(([type, data]) => (
                  <TableRow key={type}>
                    <TableCell>{INVOICE_TYPE_LABELS[type] || type}</TableCell>
                    <TableCell align="right">{data.count}</TableCell>
                    <TableCell align="right">{formatCurrency(data.tax)}</TableCell>
                    <TableCell align="right">{formatCurrency(data.total)}</TableCell>
                  </TableRow>
                ))}
              {Object.keys(summary.byType).length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={4} align="center" color="text.secondary">
                    {t('taxReport.noData', 'No data for selected period')}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* Tax by month */}
      {monthOrder.length > 0 && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="h6">
              {t('taxReport.taxByMonth', 'Tax by Month')}
            </Typography>
            <Tooltip title={t('common.print', 'Print')}>
              <IconButton size="small" onClick={() => handlePrintTable(tableByMonthRef, t('taxReport.taxByMonth', 'Tax by Month'))}>
                <PrintIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
          <TableContainer ref={tableByMonthRef}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>{t('taxReport.month', 'Month')}</TableCell>
                  <TableCell align="right">{t('taxReport.invoiceCount', 'Invoices')}</TableCell>
                  <TableCell align="right">{t('taxReport.totalTax', 'Tax')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {monthOrder.reverse().map((monthKey) => {
                  const d = summary.byMonth[monthKey];
                  const [y, m] = monthKey.split('-');
                  const monthLabel = new Date(Number(y), Number(m) - 1, 1).toLocaleDateString(undefined, {
                    month: 'short',
                    year: 'numeric',
                  });
                  return (
                    <TableRow key={monthKey}>
                      <TableCell>{monthLabel}</TableCell>
                      <TableCell align="right">{d.count}</TableCell>
                      <TableCell align="right">{formatCurrency(d.tax)}</TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Invoice-level detail */}
      <Paper sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="h6">
            {t('taxReport.invoiceDetail', 'Invoice Detail')}
          </Typography>
          <Tooltip title={t('common.print', 'Print')}>
            <IconButton size="small" onClick={() => handlePrintTable(tableDetailRef, t('taxReport.invoiceDetail', 'Invoice Detail'))}>
              <PrintIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Stack>
        {loading ? (
          <Stack alignItems="center" py={4}>
            <CircularProgress />
          </Stack>
        ) : (
          <TableContainer sx={{ maxHeight: 440 }} ref={tableDetailRef}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>{t('taxReport.invoiceNumber', 'Invoice #')}</TableCell>
                  <TableCell>{t('taxReport.date', 'Date')}</TableCell>
                  <TableCell>{t('taxReport.type', 'Type')}</TableCell>
                  <TableCell>{t('taxReport.status', 'Status')}</TableCell>
                  <TableCell align="right">{t('taxReport.subtotal', 'Subtotal')}</TableCell>
                  <TableCell align="right">{t('taxReport.tax', 'Tax')}</TableCell>
                  <TableCell align="right">{t('taxReport.total', 'Total')}</TableCell>
                  <TableCell align="right">{t('taxReport.taxRate', 'Rate')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {invoices.map((inv) => {
                  const a = getAttr(inv);
                  const rate = a.taxRate != null
                    ? (Number(a.taxRate) > 1 ? `${a.taxRate}%` : `${(Number(a.taxRate) * 100).toFixed(1)}%`)
                    : '—';
                  return (
                    <TableRow
                      key={inv.id}
                      hover
                      sx={{ cursor: 'pointer' }}
                      onClick={() => navigate(`/invoices/${inv.id}`)}
                    >
                      <TableCell>{a.invoiceNumber}</TableCell>
                      <TableCell>{formatDate(a.invoiceDate)}</TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={INVOICE_TYPE_LABELS[a.invoiceType] || a.invoiceType || '—'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={(a.status || 'draft').replace('_', ' ')}
                          color={
                            a.status === 'paid' ? 'success' :
                            a.status === 'cancelled' ? 'error' :
                            a.status === 'overdue' ? 'error' : 'default'
                          }
                        />
                      </TableCell>
                      <TableCell align="right">{formatCurrency(a.subtotal)}</TableCell>
                      <TableCell align="right">{formatCurrency(a.tax)}</TableCell>
                      <TableCell align="right">{formatCurrency(a.total)}</TableCell>
                      <TableCell align="right">{rate}</TableCell>
                    </TableRow>
                  );
                })}
                {invoices.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={8} align="center" color="text.secondary">
                      {t('taxReport.noData', 'No data for selected period')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

    </Box>
  );
};

export default TaxReport;
